import { useMemo } from 'react';
import { Icon } from '../chrome/Icon';
import { ACTIVITY_CATEGORY_META, subcategoryLabel } from './activityMeta';
import { useT } from '../../i18n';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useCatalogOrBlob } from '../media/useMediaBlob';
import { useSetActivityState } from '../../data/queries';
import type { ActivitySummary, MediaRow } from '../../data/dto';

interface Props {
  activity: ActivitySummary;
  onOpen: () => void;
  /** Compact variant for horizontal suggestion carousels. */
  compact?: boolean;
  babyId?: string | null;
}

/** A single activity row/card used in the list and suggestion sections. */
export function ActivityCard({ activity: a, onOpen, compact, babyId }: Props) {
  const meta = (a.category && ACTIVITY_CATEGORY_META[a.category]) || {
    icon: 'play' as const,
    label: a.category || 'Play',
    color: '#D98E5A',
  };
  const sub = subcategoryLabel(a.subcategory);
  const t = useT();
  const enc = useEncryption();
  const setState = useSetActivityState();

  const isFavorite = !!a.is_favorite;

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!babyId) return;
    setState.mutate({
      babyId,
      activityId: a.id,
      is_favorite: !isFavorite,
    });
  };

  const primaryMedia = useMemo(() => {
    if (!a.media) return null;
    const nonAudioMedia = a.media.filter(
      (m) => m.media_type !== 'audio' && m.variant !== 'song_full' && m.variant !== 'song_instrumental' && m.variant !== 'audio'
    );
    return nonAudioMedia.find((m) => m.is_primary) || nonAudioMedia[0] || null;
  }, [a.media]);

  const { url } = useCatalogOrBlob(
    primaryMedia
      ? ({
          id: primaryMedia.id,
          mime_type: primaryMedia.mime_type,
          is_e2e_encrypted: primaryMedia.is_e2e_encrypted,
        } as MediaRow)
      : ({} as MediaRow),
    true, // a.media is editorial activity catalog → public cacheable URL
    enc.status === 'ready',
    enc.decrypt,
    !!primaryMedia,
    true,
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={[
        'relative overflow-hidden rounded-xl border border-line bg-surface flex items-stretch w-full cursor-pointer min-h-24 text-left transition-colors hover:bg-surface/80 outline-none',
        compact ? 'w-[260px] shrink-0 snap-start' : '',
      ].join(' ')}
    >
      {/* Left 1/3: Thumbnail or Category Icon */}
      <div
        className="w-1/3 relative shrink-0 overflow-hidden flex items-center justify-center"
        style={{ background: url ? 'var(--color-tint)' : `${meta.color}14` }}
      >
        {url ? (
          <img src={url} alt="" className="absolute inset-0 size-full object-cover object-center" />
        ) : (
          <Icon name={meta.icon} size={32} color={meta.color} />
        )}

        {/* Category Overlay */}
        <div 
          className="absolute top-2 left-2 z-10 rounded-md bg-surface/85 backdrop-blur-[2px] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] shadow-xs max-w-[calc(100%-16px)] truncate" 
          style={{ color: meta.color }}
          title={t('activities.category.' + a.category, meta.label)}
        >
          {t('activities.category.' + a.category, meta.label)}
        </div>

        {/* Heart Favorite button overlay */}
        {babyId && (
          <button
            onClick={toggleFavorite}
            className={[
              'absolute top-2 right-2 z-15 size-8 flex items-center justify-center rounded-full backdrop-blur-md transition-all shadow-xs outline-none',
              isFavorite
                ? 'bg-accent-soft text-accent border border-accent/20'
                : 'bg-white/80 text-ink-3 hover:text-ink-2 border border-line/10',
            ].join(' ')}
          >
            <HeartIcon filled={isFavorite} size={13} />
          </button>
        )}
      </div>

      {/* Right 2/3: Name, AI tag, Description, StateBadge */}
      <div className="w-2/3 flex flex-col justify-between p-3.5 pr-4 pl-3.5 min-w-0">
        <div className="flex flex-col gap-1">
          {/* Top row: subcategory / category info and AI tag */}
          <div className="flex items-center justify-between gap-2 min-h-5">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink-3">
              {sub && <span className="text-[10.5px] font-medium text-ink-3">{t('activities.subcategory.' + a.subcategory, sub)}</span>}
            </div>
            {a.source === 'ai' && (
              <span className="rounded bg-accent-soft px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-accent-ink -mt-1 -mr-1">
                AI
              </span>
            )}
          </div>

          {/* Name */}
          <h3 className="font-serif text-[17px] leading-snug tracking-tight text-ink font-semibold">
            {a.title}
          </h3>

          {/* Description - max 3 lines */}
          <p className="text-[13px] leading-snug text-ink-2 text-pretty line-clamp-3">
            {a.description}
          </p>
        </div>

        {a.state && (
          <div className="mt-2.5">
            <StateBadge state={a.state} />
          </div>
        )}
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: NonNullable<ActivitySummary['state']> }) {
  const t = useT();
  if (state === 'done') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-ink">
        <Icon name="check" size={11} color="var(--color-accent-ink)" />
        {t('activities.state.done', 'Done')}
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-ink">
        {t('activities.state.saved', 'Saved')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-grey-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-3">
      {t('activities.state.notInterested', 'Not interested')}
    </span>
  );
}

function HeartIcon({ filled, size = 18, className }: { filled: boolean; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}
